import { GridBackground } from '@/components/common/GridBackground';
import { Header } from '@/components/dashboard/Header';
import { useDatasources } from '@/lib/hooks/use-datasources';
import { useFinalizedProcessesQuery } from '@/lib/hooks/use-validation-process';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Download, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_authenticated/reports')({
  component: ReportsPage,
});

function ReportsPage() {
  const { data: finalizedProcesses = [], isLoading } =
    useFinalizedProcessesQuery();
  const { data: datasources = [] } = useDatasources();
  // Le PDF est produit en imprimant la page rapport chargée dans une iframe
  // cachée — le détail du rapport n'est pas exposé en navigation.
  const [printingProcessId, setPrintingProcessId] = useState<string | null>(
    null,
  );
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);

  useEffect(() => {
    if (!isPreparingPdf) {
      return;
    }

    const timeout = setTimeout(() => setIsPreparingPdf(false), 2500);
    return () => clearTimeout(timeout);
  }, [isPreparingPdf]);

  const datasourceName = (datasourceId: string) =>
    datasources.find((d) => d.id === datasourceId)?.name ?? datasourceId;

  const handleDownloadPdf = (processId: string) => {
    setPrintingProcessId(null);
    // Remonte l'iframe au prochain rendu, même pour un second téléchargement
    // du même rapport.
    requestAnimationFrame(() => {
      setPrintingProcessId(processId);
      setIsPreparingPdf(true);
    });
  };

  return (
    <div className="min-h-screen w-full bg-[#FEFAEF] p-4 relative">
      <GridBackground />

      <div className="relative">
        <div className="hidden sm:block container mx-auto">
          <Header
            showProgress={false}
            pendingTaskCount={0}
            totalTaskCount={0}
            verificationCompleted={false}
          />
        </div>

        <main className="container mx-auto mt-8">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au tableau de bord
            </Link>
          </div>

          <div className="bg-white border border-[#C4B3E8] rounded-lg shadow-sm p-4 sm:p-6 space-y-4 sm:space-y-6">
            <h1 className="font-display font-bold text-[20px] sm:text-[22px] leading-[1.4] sm:leading-[1.27] tracking-[-2.5%] text-[#2D0A50]">
              Rapports de validation
            </h1>

            {isLoading && (
              <p className="font-archivo text-sm text-[#6B7280]">
                Chargement des rapports...
              </p>
            )}

            {!isLoading && finalizedProcesses.length === 0 && (
              <p className="font-archivo text-sm text-[#6B7280]">
                Aucun rapport finalisé pour le moment. Les rapports apparaissent
                ici dès qu'une vérification est finalisée.
              </p>
            )}

            {finalizedProcesses.length > 0 && (
              <ul className="divide-y divide-[#E5E7EB]">
                {finalizedProcesses.map((process) => (
                  <li
                    key={process.processId}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-4 px-2"
                  >
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#EFEDFE] shrink-0">
                      <FileText className="w-5 h-5 text-[#2D0A50]" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-archivo font-medium text-[15px] text-[#09090B] truncate">
                        {datasourceName(process.datasourceId)}
                      </span>
                      <span className="block font-archivo text-[13px] text-[#6B7280]">
                        Finalisé le{' '}
                        {new Date(process.finalizedAt).toLocaleDateString(
                          'fr-FR',
                          {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          },
                        )}
                      </span>
                    </span>
                    <span className="flex flex-wrap items-center gap-2 shrink-0">
                      <span className="flex items-center h-[26px] px-3 py-1 bg-[#F3F4F6] border border-[#E5E7EB] rounded-full font-archivo font-semibold text-xs text-[#374151]">
                        {process.questionCount} question
                        {process.questionCount > 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center h-[26px] px-3 py-1 bg-[#D4F6F4] border border-[#6FE3D4] rounded-full font-archivo font-semibold text-xs text-[#0A6E64]">
                        {process.changedQuestionCount} modifiée
                        {process.changedQuestionCount > 1 ? 's' : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(process.processId)}
                        disabled={isPreparingPdf}
                        className="cursor-pointer flex items-center gap-2 px-4 py-2 rounded-full bg-[#2D0A50] hover:bg-[#110441] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isPreparingPdf &&
                        printingProcessId === process.processId ? (
                          <span className="w-4 h-4 animate-spin border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <Download className="w-4 h-4 text-[#FAFAFA]" />
                        )}
                        <span className="font-archivo font-medium text-[13px] leading-[1.43] text-[#FAFAFA]">
                          Télécharger le PDF
                        </span>
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>

      {printingProcessId && (
        <iframe
          key={printingProcessId}
          title="Génération du PDF du rapport"
          src={`/report/${printingProcessId}?simplified=true&autoprint=true`}
          className="fixed bottom-0 right-0 h-0 w-0 border-0"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
